import { Injectable, signal } from '@angular/core';
import { BackendService } from '../backend/backend.service';
import { environment } from '@env';
import { HttpHeaders } from '@angular/common/http';
import { of, Subject, Subscription, timeout, TimeoutError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

interface CommandErrorResponse {
  errorCode?: string;
  message?: string;
  reference?: string;
  timestamp?: string;
  traceId?: string | null;
  isError?: boolean;
}

interface CommandResponse extends CommandErrorResponse {
  output: string[];
}

export const COMMAND_TIMEOUT_MS = 60_000;

@Injectable({
  providedIn: 'root',
})
export class TerminalService {
  latestResponse?: CommandResponse;

  responseHandler: Record<string, Function> = {
    next: this.getResultHandler.bind(this),
  };

  // Emits the output entry (HTML) of each finished command
  responseSource = new Subject<string>();
  responseCalled$ = this.responseSource.asObservable();

  terminalHistory: string[] = [];

  // Kept here rather than in the component, so a command in flight survives navigating away from the terminal page
  readonly busy = signal(false);

  commandStartedAt = 0;

  private pendingCommand?: Subscription;

  constructor(private backend: BackendService) {}

  sendCmd(command: string) {
    if (this.busy()) {
      return;
    }

    this.busy.set(true);
    this.commandStartedAt = Date.now();

    this.pendingCommand = this.backend
      .post(
        `${environment.shellEndpoint}/command`,
        { command },
        {
          headers: new HttpHeaders({
            'content-type': 'application/json',
          }),
        },
      )
      .pipe(
        map((response) => response),
        timeout(COMMAND_TIMEOUT_MS),
        catchError((errorRes) =>
          of(
            errorRes instanceof TimeoutError
              ? {
                  output: [],
                  message: `No response after ${COMMAND_TIMEOUT_MS / 1000}s. The command may still be running on the server.`,
                  isError: true,
                }
              : { ...errorRes, isError: true },
          ),
        ),
        finalize(() => this.busy.set(false)),
      )
      .subscribe({
        next: (result) => {
          this.responseHandler['next'](result);
        },
      });
  }

  // Stops waiting for the current command. The command itself cannot be cancelled on the server.
  detach() {
    if (!this.busy()) {
      return;
    }

    this.pendingCommand?.unsubscribe();
    this.getResultHandler({
      output: [],
      message: '^C (detached: the command may still be running on the server)',
      isError: true,
    });
  }

  getResultHandler(result: CommandResponse) {
    // Unlock before emitting, so subscribers can focus the input line right away
    this.busy.set(false);
    this.latestResponse = result;

    const { output, isError, message } = result ?? {};

    let outputMessage: string[] = output ?? [];

    if (!outputMessage?.length) {
      outputMessage = message ? [message] : ['Unknown error. Please check logs'];
    }

    const template = isError
      ? '<div class="prev-command error">' + outputMessage.join('\n') + '</div>'
      : '<div class="prev-command">' + outputMessage.join('\n') + '</div>';

    this.terminalHistory.push(template);
    this.responseSource.next(template);
  }
}
